import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { 
  Calendar, MapPin, ArrowLeft, ArrowRight, Share2, 
  MessageCircle, Send, User, ChevronLeft, ChevronRight,
  Facebook, Twitter, Linkedin, Link as LinkIcon, Check
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsDetailPage = () => {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [comments, setComments] = useState([]);
  const [adjacent, setAdjacent] = useState({ prev: null, next: null });
  const [loading, setLoading] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [commentForm, setCommentForm] = useState({
    nome: "",
    email: "",
    contenuto: ""
  });

  useEffect(() => {
    fetchNewsData();
  }, [newsId]);

  const fetchNewsData = async () => {
    setLoading(true);
    try {
      const [newsRes, commentsRes, adjacentRes] = await Promise.all([
        axios.get(`${API}/news/${newsId}`),
        axios.get(`${API}/news/${newsId}/comments`),
        axios.get(`${API}/news/${newsId}/adjacent`)
      ]);
      setNews(newsRes.data);
      setComments(commentsRes.data);
      setAdjacent(adjacentRes.data);
    } catch (error) {
      console.error("Error fetching news:", error);
      if (error.response?.status === 404) {
        navigate("/news");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d MMMM yyyy", { locale: it });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d MMM yyyy 'alle' HH:mm", { locale: it });
    } catch {
      return dateStr;
    }
  };

  const handleCommentChange = (field, value) => {
    setCommentForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!commentForm.nome.trim() || !commentForm.contenuto.trim()) {
      toast.error("Inserisci nome e commento");
      return;
    }

    setSubmittingComment(true);
    try {
      const response = await axios.post(`${API}/news/${newsId}/comments`, commentForm);
      setComments(prev => [response.data, ...prev]);
      setCommentForm({ nome: "", email: "", contenuto: "" });
      toast.success("Commento pubblicato!");
    } catch (error) {
      toast.error("Errore durante l'invio del commento");
    } finally {
      setSubmittingComment(false);
    }
  };

  const shareUrl = window.location.href;
  const shareTitle = news?.titolo || "";

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareTitle + " " + shareUrl)}`
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copiato!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-10">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-1/4 mb-6" />
            <div className="aspect-[2/1] bg-muted rounded-xl mb-8" />
            <div className="h-10 bg-muted rounded w-3/4 mb-4" />
            <div className="h-4 bg-muted rounded w-1/3 mb-8" />
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded w-full" />
              <div className="h-4 bg-muted rounded w-full" />
              <div className="h-4 bg-muted rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-medium mb-2">News non trovata</h2>
          <Link to="/news" className="text-primary hover:underline">
            Torna al blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="news-detail-page">
      {/* Back Link */}
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link 
            to="/news" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna al Blog
          </Link>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-4 py-10">
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="rounded-md">
              {news.categoria || "News"}
            </Badge>
            {news.regione && (
              <Badge variant="outline" className="rounded-md flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {news.regione}
              </Badge>
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-4 leading-tight">
            {news.titolo}
          </h1>
          
          <div className="flex items-center gap-4 text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatDate(news.data_pubblicazione)}
            </span>
            <span className="flex items-center gap-1.5">
              <MessageCircle className="w-4 h-4" />
              {comments.length} commenti
            </span>
          </div>
        </header>

        {/* Featured Image */}
        {news.immagine_url && (
          <div className="aspect-[2/1] rounded-xl overflow-hidden mb-10">
            <img
              src={news.immagine_url}
              alt={news.titolo}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content - Renders HTML from rich text editor */}
        <div 
          className="prose prose-lg max-w-none mb-10 prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-a:text-primary prose-img:rounded-lg prose-img:my-6"
          dangerouslySetInnerHTML={{ __html: news.contenuto }}
        />

        {/* Share Section */}
        <div className="bg-muted/30 rounded-xl p-6 mb-10">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Condividi questo articolo
          </h3>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg gap-2"
              onClick={() => window.open(shareLinks.facebook, '_blank')}
            >
              <Facebook className="w-4 h-4 text-blue-600" />
              Facebook
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg gap-2"
              onClick={() => window.open(shareLinks.twitter, '_blank')}
            >
              <Twitter className="w-4 h-4 text-sky-500" />
              Twitter
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg gap-2"
              onClick={() => window.open(shareLinks.linkedin, '_blank')}
            >
              <Linkedin className="w-4 h-4 text-blue-700" />
              LinkedIn
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg gap-2"
              onClick={() => window.open(shareLinks.whatsapp, '_blank')}
            >
              <MessageCircle className="w-4 h-4 text-green-500" />
              WhatsApp
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg gap-2"
              onClick={copyLink}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-500" />
                  Copiato!
                </>
              ) : (
                <>
                  <LinkIcon className="w-4 h-4" />
                  Copia link
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Navigation to other news */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          {adjacent.prev ? (
            <Link 
              to={`/news/${adjacent.prev.id}`}
              className="group p-5 rounded-xl border border-border hover:border-primary/30 hover:bg-muted/30 transition-all"
            >
              <span className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                <ChevronLeft className="w-4 h-4" />
                Articolo precedente
              </span>
              <h4 className="font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
                {adjacent.prev.titolo}
              </h4>
            </Link>
          ) : (
            <div />
          )}
          
          {adjacent.next && (
            <Link 
              to={`/news/${adjacent.next.id}`}
              className="group p-5 rounded-xl border border-border hover:border-primary/30 hover:bg-muted/30 transition-all text-right"
            >
              <span className="text-xs text-muted-foreground flex items-center justify-end gap-1 mb-2">
                Articolo successivo
                <ChevronRight className="w-4 h-4" />
              </span>
              <h4 className="font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
                {adjacent.next.titolo}
              </h4>
            </Link>
          )}
        </div>

        <Separator className="mb-10" />

        {/* Comments Section */}
        <section id="comments">
          <h2 className="text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
            <MessageCircle className="w-6 h-6" />
            Commenti ({comments.length})
          </h2>

          {/* Comment Form */}
          <Card className="border-0 shadow-subtle mb-8">
            <CardContent className="p-6">
              <h3 className="font-medium text-foreground mb-4">Lascia un commento</h3>
              <form onSubmit={handleSubmitComment} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="comment-nome">Nome *</Label>
                    <Input
                      id="comment-nome"
                      value={commentForm.nome}
                      onChange={(e) => handleCommentChange("nome", e.target.value)}
                      placeholder="Il tuo nome"
                      className="mt-1.5"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="comment-email">Email (opzionale)</Label>
                    <Input
                      id="comment-email"
                      type="email"
                      value={commentForm.email}
                      onChange={(e) => handleCommentChange("email", e.target.value)}
                      placeholder="La tua email"
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="comment-contenuto">Commento *</Label>
                  <Textarea
                    id="comment-contenuto"
                    value={commentForm.contenuto}
                    onChange={(e) => handleCommentChange("contenuto", e.target.value)}
                    placeholder="Scrivi il tuo commento..."
                    rows={4}
                    className="mt-1.5"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="rounded-lg gap-2"
                  disabled={submittingComment}
                >
                  {submittingComment ? (
                    "Invio in corso..."
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Pubblica commento
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Comments List */}
          {comments.length > 0 ? (
            <div className="space-y-4">
              {comments.map((comment) => (
                <Card key={comment.id} className="border-0 shadow-subtle">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="font-medium text-foreground">{comment.nome}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDateTime(comment.data_creazione)}
                          </span>
                        </div>
                        <p className="text-muted-foreground whitespace-pre-wrap">
                          {comment.contenuto}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-muted/30 rounded-xl">
              <MessageCircle className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                Nessun commento ancora. Sii il primo a commentare!
              </p>
            </div>
          )}
        </section>
      </article>
    </div>
  );
};

export default NewsDetailPage;
